import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";

const AUDIT_LOG_URL = "mst-admin/audit-log";

const ACTION_COLORS = {
  CREATE: "bg-green-100 text-green-700",
  UPDATE: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
  LOGIN: "bg-gray-100 text-gray-600",
  BROADCAST: "bg-purple-100 text-purple-700",
  PROMO_VALIDATE: "bg-yellow-100 text-yellow-700",
};

const ACTIONS = ["", "CREATE", "UPDATE", "DELETE", "LOGIN", "BROADCAST", "PROMO_VALIDATE"];

const AuditLogList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ action: "", from: "", to: "" });
  const [applied, setApplied] = useState({ action: "", from: "", to: "" });

  const fetchLogs = useCallback(async (page, f) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (f.action) params.append("action", f.action);
    if (f.from) params.append("from", f.from);
    if (f.to) params.append("to", f.to);

    const res = await func.get(`${AUDIT_LOG_URL}?${params.toString()}`);
    if (res?.status === 200) {
      setLogs(res.data?.logs || []);
      setTotalPages(res.data?.totalPages || 1);
    } else {
      toast.error("Failed to load audit log");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLogs(currentPage, applied);
  }, [currentPage, applied, fetchLogs]);

  const handleSearch = () => {
    setCurrentPage(1);
    setApplied({ ...filters });
  };

  const handleReset = () => {
    const empty = { action: "", from: "", to: "" };
    setFilters(empty);
    setApplied(empty);
    setCurrentPage(1);
  };

  const fmtDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const truncate = (str, n = 12) => str?.length > n ? str.slice(0, n) + "…" : str;

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <FontAwesomeIcon icon={faShieldAlt} className="text-white text-lg" />
          <h1 className="text-xl font-bold text-white">Audit Log</h1>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-lg shadow-md px-5 py-4 mb-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
              >
                <option value="">All Actions</option>
                {ACTIONS.filter(Boolean).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">From</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">To</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSearch} />
              Search
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Timestamp", "Admin", "Action", "Resource", "Resource ID", "IP"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(10)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                        {fmtDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {log.adminEmail || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-600"}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{log.resource || "-"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {truncate(log.resourceId)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{log.ip || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogList;
